import { RichTextEditor } from "@msys/ui";
import type { GridRenderEditCellParams } from "@mui/x-data-grid-premium";
import { useTranslate } from "@tolgee/react";
import { convertFromRaw, type RawDraftContentState } from "draft-js";
import React from "react";
import { NoPropagateBox } from "../../../commons/NoPropagateBox.js";
import { cleanHTML, draftStateToHtml } from "../../../utils.js";

export const DataGridRichTextHtmlEditCell = ({
  id,
  field,
  value,
  api,
}: GridRenderEditCellParams) => {
  const { t } = useTranslate(["Global"]);
  const pendingContentRef = React.useRef<RawDraftContentState | null>(null);

  const handleChange = React.useCallback((content: RawDraftContentState) => {
    pendingContentRef.current = content;
  }, []);

  const save = React.useCallback(async () => {
    if (pendingContentRef.current) {
      const newValue = cleanHTML(
        draftStateToHtml(convertFromRaw(pendingContentRef.current))
      );
      await api.setEditCellValue({ id, field, value: newValue });
    }
    api.stopCellEditMode({ id, field });
  }, [id, field, api]);

  const handleBlur = React.useCallback(
    async (e: React.FocusEvent) => {
      if (!e.currentTarget.contains(e.relatedTarget as Node)) {
        await save();
      }
    },
    [save]
  );

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        api.stopCellEditMode({ id, field, ignoreModifications: true });
      }
    },
    [id, field, api]
  );

  return (
    <NoPropagateBox
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        width: "100%",
        height: "100%",
      }}
    >
      <RichTextEditor
        htmlContent={value ?? ""}
        saveButtonLabel={t("Save", { ns: "Global" })}
        cancelButtonLabel={t("Cancel", { ns: "Global" })}
        size="small"
        onChange={handleChange}
        isEditing={true}
        autoFocus={true}
      />
    </NoPropagateBox>
  );
};
